import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Skeleton,
  Chip,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PublicIcon from '@mui/icons-material/Public';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../lib/AuthContext';

/**
 * Plan display configuration
 */
const PLAN_CONFIG = {
  FREE: {
    label: 'Free',
    color: 'default' as const,
    description: 'Basic monitoring for personal projects',
  },
  PRO: {
    label: 'Pro',
    color: 'secondary' as const,
    description: 'Advanced features for professionals',
  },
};

/**
 * Usage progress bar component
 */
function UsageProgress({
  label,
  used,
  limit,
  unlimited,
  icon,
}: {
  label: string;
  used: number;
  limit: number;
  unlimited: boolean;
  icon: React.ReactNode;
}) {
  const percentage = unlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isNearLimit = !unlimited && percentage >= 80;
  const isAtLimit = !unlimited && percentage >= 100;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: isAtLimit ? 'error.main' : isNearLimit ? 'warning.main' : 'text.primary',
          }}
        >
          {used} / {unlimited ? 'Unlimited' : limit}
        </Typography>
      </Box>
      {!unlimited && (
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: isAtLimit
                ? 'error.main'
                : isNearLimit
                  ? 'warning.main'
                  : 'primary.main',
            },
          }}
        />
      )}
    </Box>
  );
}

/**
 * Settings page component
 * User account and application settings
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch usage stats
  const { data: usageStats, isLoading: isLoadingStats } = trpc.settings.getUsageStats.useQuery();

  // Mutations
  const changePasswordMutation = trpc.settings.changePassword.useMutation({
    onSuccess: () => {
      setPasswordSuccess('Password changed successfully');
      setPasswordError(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error) => {
      setPasswordError(error.message);
      setPasswordSuccess(null);
    },
  });

  const deleteAccountMutation = trpc.settings.deleteAccount.useMutation({
    onSuccess: () => {
      logout();
      navigate('/');
    },
    onError: (error) => {
      setDeleteError(error.message);
    },
  });

  /**
   * Handle password change
   */
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validation
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
    });
  };

  /**
   * Handle account deletion
   */
  const handleDeleteAccount = async () => {
    setDeleteError(null);

    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      setDeleteError('Please type DELETE MY ACCOUNT to confirm');
      return;
    }

    deleteAccountMutation.mutate({
      password: deletePassword,
      confirmation: 'DELETE MY ACCOUNT',
    });
  };

  const planConfig = usageStats?.plan ? PLAN_CONFIG[usageStats.plan as keyof typeof PLAN_CONFIG] : null;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account and subscription
      </Typography>

      {/* Account Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Account Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={user?.email || ''}
              disabled
              helperText="Email cannot be changed"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Account ID"
              value={user?.id || ''}
              disabled
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Plan & Usage Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Plan & Usage
          </Typography>
          {planConfig && (
            <Chip
              icon={<WorkspacePremiumIcon />}
              label={planConfig.label}
              color={planConfig.color}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {isLoadingStats ? (
          <Box>
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={8} sx={{ mb: 3, borderRadius: 4 }} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={8} sx={{ mb: 3, borderRadius: 4 }} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 4 }} />
          </Box>
        ) : usageStats ? (
          <>
            {planConfig && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {planConfig.description}
              </Typography>
            )}

            <UsageProgress
              label="Monitors"
              used={usageStats.monitors.used}
              limit={usageStats.monitors.limit}
              unlimited={usageStats.monitors.unlimited}
              icon={<MonitorHeartIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
            />

            <UsageProgress
              label="Notification Channels"
              used={usageStats.channels.used}
              limit={usageStats.channels.limit}
              unlimited={usageStats.channels.unlimited}
              icon={<NotificationsIcon sx={{ fontSize: 20, color: 'secondary.main' }} />}
            />

            <UsageProgress
              label="Status Pages"
              used={usageStats.statusPages.used}
              limit={usageStats.statusPages.limit}
              unlimited={usageStats.statusPages.unlimited}
              icon={<PublicIcon sx={{ fontSize: 20, color: 'success.main' }} />}
            />

            {usageStats.plan === 'FREE' && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Need more resources?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upgrade to Pro for faster monitoring, SMS notifications, and more features.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<WorkspacePremiumIcon />}
                  onClick={() => navigate('/billing')}
                >
                  Upgrade to Pro
                </Button>
              </Box>
            )}
          </>
        ) : null}
      </Paper>

      {/* Change Password Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <LockIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Change Password
          </Typography>
        </Box>

        {passwordError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {passwordError}
          </Alert>
        )}

        {passwordSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {passwordSuccess}
          </Alert>
        )}

        <Box component="form" onSubmit={handleChangePassword}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                helperText="Must be at least 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                error={confirmPassword !== '' && confirmPassword !== newPassword}
                helperText={
                  confirmPassword !== '' && confirmPassword !== newPassword
                    ? 'Passwords do not match'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  changePasswordMutation.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={
                  changePasswordMutation.isLoading ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword
                }
              >
                {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Danger Zone */}
      <Paper sx={{ p: 4, border: '1px solid', borderColor: 'error.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmberIcon sx={{ color: 'error.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
            Danger Zone
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Irreversible actions that affect your account
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Delete Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Permanently delete your account and all associated data including monitors, status
              pages, and notification channels.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Account
          </Button>
        </Box>
      </Paper>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletePassword('');
          setDeleteConfirmation('');
          setDeleteError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteForeverIcon />
          Delete Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            This action is <strong>permanent and cannot be undone</strong>. All your data will be
            deleted including:
          </DialogContentText>

          <Box
            component="ul"
            sx={{
              pl: 2,
              mb: 3,
              '& li': {
                mb: 0.5,
              },
            }}
          >
            <li>
              <Typography variant="body2">All monitors and their check history</Typography>
            </li>
            <li>
              <Typography variant="body2">All status pages</Typography>
            </li>
            <li>
              <Typography variant="body2">All notification channels</Typography>
            </li>
            <li>
              <Typography variant="body2">All incident records</Typography>
            </li>
          </Box>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Your Password"
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            sx={{ mb: 2 }}
            autoComplete="current-password"
          />

          <TextField
            fullWidth
            label="Type DELETE MY ACCOUNT to confirm"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            error={deleteConfirmation !== '' && deleteConfirmation !== 'DELETE MY ACCOUNT'}
            helperText="This confirmation is required to delete your account"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletePassword('');
              setDeleteConfirmation('');
              setDeleteError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={
              deleteAccountMutation.isLoading ||
              !deletePassword ||
              deleteConfirmation !== 'DELETE MY ACCOUNT'
            }
            startIcon={
              deleteAccountMutation.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteForeverIcon />
              )
            }
          >
            {deleteAccountMutation.isLoading ? 'Deleting...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SettingsPage;
